import { useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { apiClient, ApiClientError } from '../../services/apiClient'
import { legalPublicClient, type LegalType, LEGAL_TYPE_LABELS } from '../../services/adminClient'
import { Modal } from '../../components/Modal'
import { ProtectedImage } from '../../components/ProtectedImage'
import {
  ALLOWED_IMAGE_TYPES,
  SIGNATURE_MAX_BYTES,
} from '../../services/configClient'
import { compressUploadImage } from '../../services/imageProcessing'
import type { StoredUser } from '../../auth/tokenStore'

export function PerfilPage() {
  const { state, refreshUser, logout } = useAuth()

  if (state.status !== 'authenticated') return null

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Perfil</h1>
        </div>
      </header>
      <NombresSection user={state.user} onSaved={refreshUser} />
      <FirmaSection user={state.user} onSaved={refreshUser} />
      <LegalSection />
      {state.user.audience === 'user' && state.user.role === 'technician' && (
        <LeaveTeamSection logout={logout} />
      )}
    </div>
  )
}

// ─── Sección 1: Nombres + empresa asociada ───────────────────────────────────

function NombresSection({ user, onSaved }: { user: StoredUser; onSaved: () => Promise<void> }) {
  const [firstName, setFirstName] = useState(user.first_name)
  const [lastNamePaternal, setLastNamePaternal] = useState(user.last_name_paternal)
  const [lastNameMaternal, setLastNameMaternal] = useState(user.last_name_maternal ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  const dirty =
    firstName !== user.first_name ||
    lastNamePaternal !== user.last_name_paternal ||
    lastNameMaternal !== (user.last_name_maternal ?? '')

  async function handleSave() {
    setError(null)
    setSavedMessage(null)
    setSaving(true)
    try {
      await apiClient.patch('/me', {
        first_name: firstName.trim(),
        last_name_paternal: lastNamePaternal.trim(),
        last_name_maternal: lastNameMaternal.trim() || null,
      })
      await onSaved()
      setSavedMessage('Guardado.')
      setTimeout(() => setSavedMessage(null), 2500)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <h3>Información personal</h3>
      <div className="form-grid">
        <label className="field">
          <span>Correo electrónico</span>
          <input value={user.email} disabled />
        </label>

        {user.audience === 'user' && user.company_name && (
          <label className="field">
            <span>Empresa</span>
            <input value={user.company_name} disabled />
          </label>
        )}

        <label className="field">
          <span>Nombre(s)</span>
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={100} disabled={saving} />
        </label>

        <div className="field-row">
          <label className="field">
            <span>Apellido paterno</span>
            <input value={lastNamePaternal} onChange={(e) => setLastNamePaternal(e.target.value)} maxLength={100} disabled={saving} />
          </label>
          <label className="field">
            <span>Apellido materno <em className="muted">(opcional)</em></span>
            <input value={lastNameMaternal} onChange={(e) => setLastNameMaternal(e.target.value)} maxLength={100} disabled={saving} />
          </label>
        </div>

        {error && <p className="error">{error}</p>}
        {savedMessage && <p className="info">{savedMessage}</p>}

        <div className="modal__actions">
          <button type="button" className="primary-btn" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </section>
  )
}

// ─── Sección 2: Firma personal ───────────────────────────────────────────────

function FirmaSection({ user, onSaved }: { user: StoredUser; onSaved: () => Promise<void> }) {
  if (user.audience !== 'user') return null

  const inputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFile(file: File) {
    setError(null)
    if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      setError('Formato no permitido. Usa PNG, JPG o WEBP.')
      return
    }
    setUploading(true)
    try {
      const processed = await compressUploadImage(file, SIGNATURE_MAX_BYTES / 1024 / 1024)
      if (processed.size > SIGNATURE_MAX_BYTES) {
        setError('El archivo excede el tamaño máximo (1 MB).')
        return
      }
      const fd = new FormData()
      fd.append('file', processed)
      await apiClient.post('/me/firma', fd)
      await onSaved()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo subir la firma.')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function handleDelete() {
    setError(null)
    setDeleting(true)
    try {
      await apiClient.delete('/me/firma')
      await onSaved()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo eliminar la firma.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <section className="card">
      <h3>Imagen de firma</h3>
      <div className="image-config-grid">
        <div className="image-uploader">
          <div className="image-uploader__header">
            <strong>Firma personal</strong>
            <small className="muted">Preferiblemente PNG con fondo transparente. Máx. 1 MB. Se incluye en los PDFs de reportes que generes.</small>
          </div>
          <div className="image-uploader__preview">
            {user.signature_path ? (
              <ProtectedImage path={user.signature_path} alt="Firma personal" className="image-uploader__img" />
            ) : (
              <span className="muted">Sin firma cargada</span>
            )}
          </div>
          {error && <p className="error">{error}</p>}
          <div className="image-uploader__actions">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
            />
            <button type="button" className="primary-btn" onClick={() => inputRef.current?.click()} disabled={uploading || deleting}>
              {uploading ? 'Subiendo…' : user.signature_path ? 'Reemplazar' : 'Subir firma'}
            </button>
            {user.signature_path && (
              <button type="button" className="danger-btn" onClick={handleDelete} disabled={uploading || deleting}>
                {deleting ? 'Eliminando…' : 'Eliminar'}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Sección 3: Documentos legales ───────────────────────────────────────────

function LegalSection() {
  const [openDoc, setOpenDoc] = useState<LegalType | null>(null)
  const [docHtml, setDocHtml] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  async function openLegal(tipo: LegalType) {
    setOpenDoc(tipo)
    setDocHtml('')
    setLoading(true)
    setFetchError(null)
    try {
      const res = await legalPublicClient.current(tipo)
      setDocHtml(res.document.content_html)
    } catch {
      setFetchError('No se pudo cargar el documento.')
    } finally {
      setLoading(false)
    }
  }

  function handleClose() {
    setOpenDoc(null)
    setDocHtml('')
    setFetchError(null)
  }

  return (
    <>
      <section className="card">
        <h3>Documentos legales</h3>
        <div className="form-grid">
          <p className="muted">Consulta los documentos legales vigentes de la plataforma.</p>
          <div className="modal__actions">
            <button type="button" className="primary-btn" onClick={() => openLegal('TOS')}>
              {LEGAL_TYPE_LABELS.TOS}
            </button>
            <button type="button" className="primary-btn" onClick={() => openLegal('PP')}>
              {LEGAL_TYPE_LABELS.PP}
            </button>
          </div>
        </div>
      </section>

      <Modal open={openDoc !== null} onClose={handleClose} title={openDoc ? LEGAL_TYPE_LABELS[openDoc] : ''} size="fullscreen">
        {loading && <p className="muted">Cargando…</p>}
        {fetchError && <p className="error">{fetchError}</p>}
        {!loading && !fetchError && docHtml && (
          <div className="legal-html" dangerouslySetInnerHTML={{ __html: docHtml }} />
        )}
      </Modal>
    </>
  )
}

// ─── Sección 4: Salirse del equipo (solo técnicos) ───────────────────────────

function LeaveTeamSection({ logout }: { logout: () => Promise<void> }) {
  const [showConfirm, setShowConfirm] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleLeave() {
    setLeaving(true)
    setError(null)
    try {
      await apiClient.post('/me/leave-team', {})
      await logout()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo procesar tu solicitud.')
      setLeaving(false)
      setShowConfirm(false)
    }
  }

  return (
    <>
      <section className="card card--danger-zone">
        <h3>Salirse del equipo</h3>
        <p className="muted">
          Al salirte del equipo perderás el acceso a todos los reportes y clientes de la empresa.
          Esta acción no se puede deshacer.
        </p>
        <div className="modal__actions">
          <button type="button" className="danger-btn" onClick={() => setShowConfirm(true)}>
            Salirme del equipo
          </button>
        </div>
      </section>

      <Modal open={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar salida del equipo">
        <div className="form-grid">
          <p>¿Estás seguro de que deseas salirte del equipo? <strong>Perderás el acceso a los reportes y clientes de la empresa.</strong></p>
          {error && <p className="error">{error}</p>}
          <div className="modal__actions">
            <button type="button" className="ghost-btn" onClick={() => setShowConfirm(false)} disabled={leaving}>
              Cancelar
            </button>
            <button type="button" className="danger-btn" onClick={handleLeave} disabled={leaving}>
              {leaving ? 'Procesando…' : 'Sí, salirme del equipo'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
